import { requestIdleCallback } from '@theme/utilities';

/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * Base class that powers our custom web components.
 *
 * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
 * the refs updated when the DOM changes. Also handles declarative event listeners using.
 *
 * @template {Refs} [T=Refs]
 * @extends {DeclarativeShadowElement}
 */
export class Component extends DeclarativeShadowElement {
  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      for (const root of this.roots) {
        this.#mutationObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['ref'],
          attributeOldValue: true,
        });
      }
    });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer.
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node - The node to get the ancestor of.
 * @returns {Node | null} The ancestor of the node or null if none is found.
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `Component`.
 *
 * @param {Node | null} node - The starting node to search from.
 * @returns {HTMLElement | null} The closest ancestor `Component` instance or null if none is found.
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * Initializes the event listeners for custom event handling.
 *
 * Sets up event listeners for specified events and delegates the handling of those events
 * to methods defined on the closest `Component` instance, based on custom attributes.
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        // Extract the last segment of the attribute value delimited by `?` or `/`
        // Do not use lookback for Safari 16.0 compatibility
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof Component) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            /** @type {(Event | Data)[]} */
            const args = [proxiedEvent];

            if (data) args.unshift(parseData(data));

            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}

(() => {
  if (window.location.pathname !== '/pages/shipping-policy') return;

  const renderShippingPolicy = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const hasCurrentPolicy = main.textContent.includes('Where do we ship?');
    const hasLegacyPageFly = main.textContent.includes('For US Warehouse') || main.querySelector('.__pf, [data-pf-type]');
    if (hasCurrentPolicy || !hasLegacyPageFly) return;

    const themeAssetLink = document.querySelector('link[href*="/cdn/shop/t/"][href*="/assets/"]');
    const cssHref = themeAssetLink
      ? themeAssetLink.href.replace(/\/assets\/[^/?#]+(\?.*)?$/, '/assets/shipping-policy.css')
      : '/cdn/shop/t/18/assets/shipping-policy.css';

    if (!document.querySelector('link[href*="shipping-policy.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssHref;
      document.head.appendChild(link);
    }

    main.dataset.template = 'page.shipping-policy';
    main.innerHTML = `
      <div class="section-background color-scheme-1"></div>
      <section class="section section--page-width color-scheme-1 shipping-policy-page spacing-style" style="--padding-block-start: max(20px, calc(var(--spacing-scale) * 28px)); --padding-block-end: max(20px, calc(var(--spacing-scale) * 72px));">
        <div class="shipping-policy-page__container">
          <header class="shipping-policy-page__header">
            <h1 class="shipping-policy-page__title">Shipping Policy</h1>
            <p class="shipping-policy-page__updated">Last updated: April 16, 2026</p>
            <div class="shipping-policy-page__intro rte">
              <p>This Shipping Policy explains where SIBRILLE ships, how orders are processed, how delivery timelines are estimated, and what customers should do if a shipment arrives damaged, delayed, or does not arrive at all.</p>
            </div>
            <p class="shipping-policy-page__contact">
              For shipping questions about an existing order, contact
              <a href="mailto:support@sibrille.com">support@sibrille.com</a>.
            </p>
          </header>

          <div class="shipping-policy-page__items">
            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">01</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">Where do we ship?</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>SIBRILLE currently ships across the continental United States and throughout Europe. Delivery availability may vary for offshore territories, P.O. boxes, military addresses, and locations affected by carrier, customs, or local import restrictions.</p>
                </div>
              </div>
            </article>

            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">02</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">How much does shipping cost?</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>Shipping charges are calculated based on the delivery destination, product size, and carrier service selected for the order. Standard destinations may qualify for free shipping promotions, while remote or hard-to-reach areas may require an additional surcharge that will be shown before checkout is completed.</p>
                </div>
              </div>
            </article>

            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">03</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">How long does shipping take and how can customers track orders?</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>Most in-stock orders are processed within 1-2 business days after payment is confirmed. Once an order has shipped, SIBRILLE sends a confirmation email with tracking information so customers can monitor delivery progress online.</p>
                  <p>Transit times vary by destination, carrier capacity, customs processing, and seasonal demand.</p>
                </div>
              </div>
            </article>

            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">04</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">What if the customer receives the wrong, damaged, or defective item?</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>If a shipment arrives with the wrong item, visible damage, or a product defect, customers should contact <a href="mailto:support@sibrille.com">support@sibrille.com</a> within 7 days of delivery. Please include the order number, a brief description of the issue, and clear photos of the product, packaging, and shipping label so the support team can review the case promptly.</p>
                </div>
              </div>
            </article>

            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">05</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">Which carriers do we use and what is the estimated delivery time?</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>SIBRILLE works with major parcel carriers and trusted regional delivery partners, depending on the destination and parcel size. Estimated delivery time is generally 3-10 business days after dispatch, but actual timing may vary by country, customs clearance, and local carrier service levels.</p>
                </div>
              </div>
            </article>

            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">06</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">Delays and unforeseen circumstances</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>Delivery timelines are estimates and may be affected by public holidays, public health emergencies, COVID-related restrictions, severe weather, customs inspections, warehouse congestion, carrier capacity limits, labor disruptions, force majeure events, or other circumstances outside SIBRILLE's direct control. When a delay occurs, we recommend monitoring the tracking link and contacting our support team if an order appears stalled for an unusual period.</p>
                </div>
              </div>
            </article>

            <article class="shipping-policy-page__item">
              <div class="shipping-policy-page__item-index">07</div>
              <div class="shipping-policy-page__item-content">
                <h2 class="shipping-policy-page__item-title">What happens if a shipment is lost?</h2>
                <div class="shipping-policy-page__answer rte">
                  <p>If tracking shows no delivery progress for an extended period or a package is confirmed lost in transit, SIBRILLE will open an investigation with the carrier. Carrier investigations typically take 5-10 business days, depending on the route and carrier involved. Once a shipment is confirmed lost, we will work with the customer on an appropriate resolution, which may include a replacement shipment or a refund.</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    `;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderShippingPolicy, { once: true });
  } else {
    renderShippingPolicy();
  }
})();
